import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { Toaster } from 'app/shared/lib/toaster';
import { AddCommonDiagnoses } from 'app/um/components/common-codes/add-common-diagnoses';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class CommonDiagnoses extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected commonDiagnosis: UM.CommonDiagnosis | null = null;
    protected commonDiagnoses: ReadonlyArray<UM.CommonDiagnosis> | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number | null = 0;
    protected pageSize: number = 25;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    protected $addCommonDiagnosesModal!: IPopup;
    protected $form!: IForm;
    protected $commonDiagnosisForm!: Element;
    protected $addCommonDiagnoses!: AddCommonDiagnoses;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$addCommonDiagnosesModal.close();

        if(detail.form.reset) detail.form.reset();
        this.$addCommonDiagnoses.reload();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadCommonDiagnoses();
    }

    protected addCommonDiagnoses(commonDiagnosis: UM.CommonDiagnosis): void {
        this.commonDiagnosis = commonDiagnosis;

        this.$addCommonDiagnosesModal.open();
    }

    protected addCommonDiagnosesClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$addCommonDiagnosesModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCommonDiagnoses(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadCommonDiagnoses(options);
            this.commonDiagnoses = response.results;
            this.resultCount = response.count;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }


    @apiCall
    protected async deleteCommonDiagnosis(commonDiagnosis: UM.CommonDiagnosis): Promise<void> {
        let isSuccessful = false;

        try {
            this.state.beginSave();
            const response = await this.service.deleteCommonDiagnosis(commonDiagnosis);
            isSuccessful = response.isSuccessful;
            this.$addCommonDiagnoses.reload();
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            const toast = isSuccessful ? this.toaster.info() : this.toaster.error();
            toast.message({
                key: isSuccessful ? 'ui:messages.events.deletedFromCommonList' : 'ui:messages.events.deleteFromCommonListFailed',
                count: 1,
                what: 'um:commonDiagnosis.$'
            }).show();
            this.state.endSave();
        }
    }
}
